import { useMapTokenPropsToCSS } from 'foundations-components/hooks/useMapTokenPropsToCSS';
import { filterTokenProps } from 'foundations-components/utils/filterTokenProps';
import { useEffect, useState } from 'react';
import ReportingActionsLoader from 'reporting-action-components/reportingActions/ReportingActionsLoader';
import { REPORT_TEMPLATE } from 'reporting-enablement/components/singleReportViewer/common/Usage';
import FiscalYearProvider from 'reporting-ui-components/components/PlatformReport/FiscalYearProvider';
import UserInfoProvider from 'reporting-ui-components/components/PlatformReport/UserInfoProvider';
import styled from 'foundations-theming/utils';
import AsyncSingleReportViewerModal from '../singleReportViewer/AsyncSingleReportViewerModal';
import { ReportCardCustomInternal } from './ReportCardCustomInternal';
import ReportCardInternal from './ReportCardInternal';
import { ADD_ONS, DISPLAY_MODES } from './constants/ReportCardOptions';
import { isReportCardReport } from './tsTypes/ReportCard';
import { useReportWithTableLimit } from '../singleReportViewer/common/hooks/useReportWithTableLimit';
import { WithEnablementTrackingProvider } from '../../monitoring/EnablementTrackingContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CardWrapper = styled.div.withConfig({
  displayName: "ReportCardV2__CardWrapper",
  componentId: "ccaw36-0"
})(["", " ", " ", ""], ({
  trellisCssStyles
}) => {
  return Object.assign({}, trellisCssStyles);
}, ({
  addOns
}) => (addOns === null || addOns === void 0 ? void 0 : addOns.includes(ADD_ONS.DISABLE_CARD_WRAPPER)) && `
    display: flex;
    flex-direction: column;
  `, ({
  displayMode
}) => displayMode === DISPLAY_MODES.DASHBOARD && `
      [data-test-id='report-card-actions'] {
        visibility: hidden;

        &[data-open='true'] {
          visibility: visible;
        }
      }

      &:focus,
      &:focus-within,
      &:hover {
        [data-test-id='report-card-actions'] {
          visibility: visible;
        }
      }
  `);
const DEFAULT_TABLE_LIMIT = 10;
export function ReportCardV2Inner(props) {
  var _props$addOns;
  const {
    report,
    displayMode = DISPLAY_MODES.DEFAULT,
    blockSize = '100%',
    minBlockSize,
    srvUse = REPORT_TEMPLATE,
    onReportChange,
    addOns,
    ToolbarOverride,
    onSRVUpdateSuccess
  } = props;
  const {
    tokenProps
  } = filterTokenProps(Object.assign({}, props, {
    blockSize
  }));
  const trellisCssStyles = useMapTokenPropsToCSS(tokenProps);
  const [isReportViewerIsOpen, setIsReportViewerIsOpen] = useState(isReportCardReport(props) ? !!props.openSRVOnLoad : false);
  const [previewReport, setPreviewReport] = useState(report);
  /**
   * Track if report is changed from report remix because we don't want to re-fetch presets if this happens.
   * This is done to prevent remix from constantly changing and allowing user to checkout different presets
   * without constantly losing old options
   * */
  const [isReportRemixChange, setIsReportRemixChange] = useState(false);
  useEffect(() => {
    setPreviewReport(report);
  }, [report]);
  function onPreviewReportChange(updatedReport, options) {
    var _options$isReportRemi;
    setPreviewReport(updatedReport);
    onReportChange === null || onReportChange === void 0 || onReportChange(updatedReport);
    setIsReportRemixChange((_options$isReportRemi = options === null || options === void 0 ? void 0 : options.isReportRemixChange) !== null && _options$isReportRemi !== void 0 ? _options$isReportRemi : false);
  }
  const reportWithTableLimit = useReportWithTableLimit({
    report,
    previewReport,
    onPreviewReportChange,
    defaultTableLimit: addOns !== null && addOns !== void 0 && addOns.includes(ADD_ONS.TABLE_LIMIT_10) ? DEFAULT_TABLE_LIMIT : undefined
  });
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx(CardWrapper, {
      "data-test-id": "report-card-v2",
      displayMode: displayMode,
      trellisCssStyles: trellisCssStyles,
      addOns: addOns,
      children: isReportCardReport(props) ? /*#__PURE__*/_jsxs(ReportingActionsLoader, {
        children: [/*#__PURE__*/_jsx(UserInfoProvider, {
          children: /*#__PURE__*/_jsx(FiscalYearProvider, {
            children: /*#__PURE__*/_jsx(ReportCardInternal, Object.assign({}, props, {
              blockSize: blockSize,
              minBlockSize: minBlockSize,
              report: reportWithTableLimit,
              setIsReportViewerIsOpen: isOpen => {
                setIsReportViewerIsOpen(isOpen);
                if (isOpen) {
                  var _props$onOpenSRV;
                  (_props$onOpenSRV = props.onOpenSRV) === null || _props$onOpenSRV === void 0 || _props$onOpenSRV.call(props);
                } else {
                  var _props$onCloseSRV;
                  (_props$onCloseSRV = props.onCloseSRV) === null || _props$onCloseSRV === void 0 || _props$onCloseSRV.call(props);
                }
              },
              setPreviewReport: onPreviewReportChange,
              ToolbarOverride: ToolbarOverride,
              isReportRemixChange: isReportRemixChange
            }))
          })
        }), isReportViewerIsOpen && ((_props$addOns = props.addOns) === null || _props$addOns === void 0 ? void 0 : _props$addOns.includes(ADD_ONS.VIEW_SRV)) && /*#__PURE__*/_jsx(AsyncSingleReportViewerModal, {
          report: report,
          previewReport: reportWithTableLimit,
          onClose: () => {
            var _props$onCloseSRV2;
            setIsReportViewerIsOpen(false);
            (_props$onCloseSRV2 = props.onCloseSRV) === null || _props$onCloseSRV2 === void 0 || _props$onCloseSRV2.call(props);
          },
          builderAccess: false,
          previewReportChanged: false,
          use: srvUse,
          filterWarning: props.filterWarning,
          onUpdateSuccess: updatedReport => {
            const shouldClose = (onSRVUpdateSuccess === null || onSRVUpdateSuccess === void 0 ? void 0 : onSRVUpdateSuccess(updatedReport)) === true;
            if (shouldClose) {
              var _props$onCloseSRV3;
              setIsReportViewerIsOpen(false);
              (_props$onCloseSRV3 = props.onCloseSRV) === null || _props$onCloseSRV3 === void 0 || _props$onCloseSRV3.call(props);
            }
          }
        })]
      }) : /*#__PURE__*/_jsx(ReportCardCustomInternal, Object.assign({}, props))
    })
  );
}
export default WithEnablementTrackingProvider(ReportCardV2Inner);